// Third Party
import * as React from 'react';
import { Form, Formik } from 'formik';

// Utils
import { useGiftCardByCertificateNumberLazyQuery } from '@gql';
import { sleep } from '@utils/sleep';
import { initialValues, FormSchema, validationSchema } from './form-schema';
import { GiftCard } from '../types';

// UI
import { Button } from '@components/button';
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { FormikInput } from '@components/form/formik-input';
import { Typography } from '@components/typography';

type Props = {
  setGiftCard: (giftCard: GiftCard | undefined) => void;
};

export const GiftCardLookupForm: React.FC<Props> = ({ setGiftCard }) => {
  const [giftCardByCertificateNumberLazyQuery] =
    useGiftCardByCertificateNumberLazyQuery();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  return (
    <div className="mt-6 text-primary-main">
      <div className="flex flex-col items-center justify-center text-center space-y-4">
        <Typography component="h1" variant="title2">
          Gift Card Lookup
        </Typography>
        <Typography component="p" variant="body1">
          Lookup your gift card balance using the form below
        </Typography>
      </div>

      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async ({ giftCardNumber }: FormSchema) => {
            setErrorMessage(undefined);
            await sleep(500);

            const result = await giftCardByCertificateNumberLazyQuery({
              variables: { certificateNumber: giftCardNumber },
            });

            if (result.data?.giftCard.__typename === 'GiftCard') {
              setGiftCard(result.data.giftCard);
            } else {
              setErrorMessage('Gift card not found.');
            }
          }}
        >
          {({ isSubmitting }) => {
            return (
              <Form className="grid gap-y-6 grid-cols-1 mx-auto py-8 sm:gap-x-8 sm:grid-cols-2 container">
                {errorMessage && (
                  <div className="p-4 bg-error-light text-error-main rounded-lg shadow-lg border-error-main border-2 mb-4 font-semibold sm:col-span-2">
                    {errorMessage}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <Typography component="p" variant="body1">
                    Your gift card redemption certificate number should be
                    entered exactly as printed on your Savvy eCard or Savvy
                    plastic gift certificate. If you have trouble with our
                    automated lookup tool, or aren&apos;t getting the results
                    you expect, please email us at{' '}
                    <Link to="mailto:giftcards@savvy.com">
                      {/* TODO: link to new savvy email address */}
                      giftcards@savvy.com
                    </Link>{' '}
                    for additional assistance. Be sure to include your gift
                    certificate number, and any additional information that will
                    help our team accurately identify your certificate.
                  </Typography>
                </div>
                <div className="flex flex-col sm:flex-row sm:col-span-2 space-y-4 sm:space-y-0 w-full">
                  <div className="w-full sm:flex-1 sm:mr-4">
                    <FormikInput
                      leftContent={<Icon name={IconNames.GIFT_CARD} />}
                      type="text"
                      name="giftCardNumber"
                      label="Gift Card Number"
                      className="w-full"
                    />
                  </div>

                  <div className="w-full sm:w-auto">
                    <Button
                      type="submit"
                      size={'lg'}
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      className="w-full sm:w-auto text-center justify-center"
                    >
                      Lookup
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-gray-main w-full">
                  *** Input is case sensitive. Please also include dashes.
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
